import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUserRole } from '@/hooks/useUserRole';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, UserCheck, Search } from 'lucide-react';
import { teamService } from '@/services/teamService';
import { toast } from 'sonner';
import PlayerManagementModal from '@/components/teams/PlayerManagementModal';

interface TeamPlayer {
  user_id: string;
  position?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  user_type?: string;
}

interface Team {
  team_id: number;
  team_name: string;
  sport?: string;
}

const TeamPlayers = () => {
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { permissions, userId } = useUserRole();
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<TeamPlayer[]>([]);
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlayerModal, setShowPlayerModal] = useState(false);

  const loadTeamData = async () => {
    if (!teamSlug) {
      // Load user's teams when no specific team is selected
      try {
        setLoading(true);
        if (userId) {
          const teams = await teamService.getUserTeams(userId);
          setUserTeams(teams);
        }
      } catch (error) {
        console.error('Error loading user teams:', error);
        toast.error('Failed to load teams');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    try {
      setLoading(true);
      const teamData = await teamService.getTeamBySlug(teamSlug);
      setTeam(teamData);
      
      if (teamData) {
        const playersData = await teamService.getTeamPlayers(teamData.team_id);
        setPlayers(playersData);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeamData();
  }, [teamSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        {isMobile ? <MobileHeader /> : <Header />}
        <main className="container mx-auto px-4 py-8" style={{paddingTop: '100px'}}>
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-1/3 mb-8"></div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-gray-800 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Handle case when no teamSlug is provided - show team selection
  if (!teamSlug) {
    return (
      <div className="min-h-screen bg-black text-white">
        {isMobile ? <MobileHeader /> : <Header />}
        <main className="container mx-auto px-4 py-8" style={{paddingTop: '100px'}}>
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-sports-green mb-6">Select a Team</h1>
            <p className="text-gray-400 mb-8">Choose a team to view and manage its players.</p>
            
            {userTeams.length > 0 ? (
              <div className="grid gap-4">
                {userTeams.map((userTeam) => (
                  <Card key={userTeam.team_id} className="bg-gray-900 border-gray-800 hover:border-sports-green transition-colors cursor-pointer"
                        onClick={() => navigate(`/team-players/${userTeam.team_name.toLowerCase().replace(/\s+/g, '-')}`)}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-2">{userTeam.team_name}</h3>
                          {userTeam.sport && (
                            <Badge className="bg-blue-600 text-white">{userTeam.sport}</Badge>
                          )}
                        </div>
                        <Users className="h-8 w-8 text-sports-green" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-300 mb-2">No Teams Found</h2>
                <p className="text-gray-400 mb-6">You don't have access to any teams yet.</p>
                <Button onClick={() => navigate('/dashboard')} className="bg-sports-green text-black hover:bg-sports-green/90">
                  Back to Dashboard
                </Button>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-black text-white">
        {isMobile ? <MobileHeader /> : <Header />}
        <main className="container mx-auto px-4 py-8" style={{paddingTop: '100px'}}>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Team Not Found</h1>
            <p className="text-gray-400">The team you're looking for doesn't exist.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {isMobile ? <MobileHeader /> : <Header />}
      <main className="container mx-auto px-4 py-8" style={{paddingTop: '100px'}}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-sports-green mb-2">
                {team.team_name} Players
              </h1>
              <p className="text-gray-400">Manage your team's player roster</p>
            </div>
            <Button
              onClick={() => setShowPlayerModal(true)}
              className="bg-sports-green text-black hover:bg-green-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Players
            </Button>
          </div>

          <div className="grid gap-6">
            {/* Team Stats */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-sports-green">{players.length}</div>
                    <div className="text-gray-400">Total Players</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {new Set(players.map(p => p.position)).size}
                    </div>
                    <div className="text-gray-400">Positions Filled</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-400">{team.sport || 'N/A'}</div>
                    <div className="text-gray-400">Sport</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Players List */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Current Roster</CardTitle>
              </CardHeader>
              <CardContent>
                {players.length > 0 ? (
                  <div className="grid gap-3">
                    {players.map((player) => (
                      <div
                        key={player.user_id}
                        className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <UserCheck className="h-5 w-5 text-green-400" />
                          <div>
                            <p className="font-medium text-white">
                              {player.username || 
                               `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
                               `Player ${player.user_id.substring(0, 8)}`}
                            </p>
                            <p className="text-sm text-gray-400">
                              {player.user_type === 'internal' ? 'Internal User' : 'Registered User'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {player.position && (
                            <Badge variant="outline" className="text-sports-green border-sports-green">
                              {player.position}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">No players added yet</p>
                    <Button
                      onClick={() => setShowPlayerModal(true)}
                      className="bg-sports-green text-black hover:bg-green-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Player
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />

      {showPlayerModal && (
        <PlayerManagementModal
          isOpen={showPlayerModal}
          onClose={() => setShowPlayerModal(false)}
          teamId={team.team_id}
          teamName={team.team_name}
          teamSport={team.sport}
          currentPlayers={players}
          onPlayersUpdate={loadTeamData}
        />
      )}
    </div>
  );
};

export default TeamPlayers;